import type { TicketResponse as AdminTicketResponse } from '@acme/api/admin';
import type { TicketResponse as ClientTicketResponse } from '@acme/api/client';
import { FileAttachmentList, formatDateTime } from '@acme/foundation';
import React from 'react';
import { Link } from 'react-router-dom';
import { TicketStatusBadge } from './ui';

export type TicketResponse = AdminTicketResponse | ClientTicketResponse;

export interface TicketCardProps {
  ticket: TicketResponse;
  onSendMessage?: () => void;
  applicationBasePath?: string;
  onDownloadAttachment?: (attachmentId: string, fileName: string) => void;
  dateField?: 'createdAt' | 'updateAt';
}

export const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  applicationBasePath,
  onDownloadAttachment,
  dateField = 'updateAt',
}) => {
  const dateValue =
    dateField === 'createdAt' ? ticket.createdAt : ticket.updateAt;
  const startMessage = ticket.startMessage as
    | {
        text?: string | null;
        attachments?: Array<{
          id?: string;
          fileName?: string | null;
        }> | null;
      }
    | null
    | undefined;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-4 mb-1">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-500">
            #{ticket.number}
          </h1>
          {ticket.applicationCode &&
            (applicationBasePath && ticket.applicationId ? (
              <Link
                to={`${applicationBasePath}/${ticket.applicationId}`}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                {ticket.applicationCode}
              </Link>
            ) : (
              <span className="text-xl font-semibold text-blue-600">
                {ticket.applicationCode}
              </span>
            ))}
        </div>
        <div className="flex items-center gap-3">
          <TicketStatusBadge statusId={ticket.status?.id} size="md" />
        </div>
      </div>
      {ticket.subject && <div className="text-lg mb-3">{ticket.subject}</div>}
      <div className="flex justify-between gap-4 mb-6">
        <span className="text-xs text-gray-500">
          {formatDateTime(dateValue) || '—'}
        </span>
        {ticket.categoryId?.name && (
          <span className="text-sm text-gray-600">
            {ticket.categoryId.name}
          </span>
        )}
      </div>
      {startMessage && (
        <>
          {startMessage.text && (
            <div className="text-sm text-gray-900">{startMessage.text}</div>
          )}
          {startMessage.attachments &&
            startMessage.attachments.length > 0 &&
            onDownloadAttachment && (
              <FileAttachmentList
                className="mt-2"
                title={null}
                attachments={startMessage.attachments}
                onDownload={onDownloadAttachment}
              />
            )}
        </>
      )}
    </div>
  );
};
